/* ==========================================================================
   3DPRO — Bundle Builder logic  (v2: cart-as-source-of-truth)
   Loaded with `defer` from sections/bundle-builder-3dpro.liquid.

   ARCHITECTURE (v3: deferred cart commit)
   - Adding/removing buckets edits LOCAL bundle state only — zero cart
     traffic — so cart-watching apps (OCU Slide Cart Drawer etc.) have
     nothing to react to and can never auto-open on bundle adds.
   - The bundle is COMMITTED to the real cart in one /cart/update.js call
     (absolute quantities per variant, incl. the tier-3 gift) only when the
     shopper clicks View cart or Checkout. The slider opening then is fine —
     that's the shopper asking for the cart.
   - On page load and whenever the cart changes elsewhere (cart page,
     slider edits, other tabs), the bundle re-hydrates from GET /cart.js —
     unless there are uncommitted local changes, which take precedence.
   - Only variants belonging to this section's products (the catalog) count
     toward the bundle; anything else in the cart is left untouched.
   ========================================================================== */
(function () {
  'use strict';

  /* Registry of refresh functions — one per bundle root on the page */
  var registry = [];

  /* Watch cart mutations made by OTHER scripts (theme slider cart, apps):
     wrap fetch once, and after any foreign /cart/add|change|update|clear
     call settles, re-hydrate the bundle. Our own writes carry the
     X-3DPRO-Bundle-Sync header and are skipped (they already refresh). */
  (function patchFetch() {
    if (window.__bbCartWatch) return;
    window.__bbCartWatch = true;
    var orig = window.fetch;
    var re = /\/cart\/(add|change|update|clear)(\.js)?/;
    window.fetch = function (input, init) {
      var url = typeof input === 'string' ? input : ((input && input.url) || '');
      var ours = false;
      try {
        var h = init && init.headers;
        ours = !!(h && (h['X-3DPRO-Bundle-Sync'] || (h.get && h.get('X-3DPRO-Bundle-Sync'))));
      } catch (e) {}
      var p = orig.apply(this, arguments);
      if (re.test(url) && !ours) {
        p.then(function () {
          clearTimeout(window.__bbCartWatchT);
          window.__bbCartWatchT = setTimeout(function () {
            registry.forEach(function (fn) { fn(); });
          }, 250);
        }).catch(function () {});
      }
      return p;
    };
  })();

  /* Same watch for XMLHttpRequest — many themes/apps (and quick-add / upsell
     widgets) add to the cart via XHR, not fetch. Without this, add-ons added
     on the page wouldn't show up in the bundle until a reload. */
  (function patchXHR() {
    if (window.__bbCartWatchXHR || !window.XMLHttpRequest) return;
    window.__bbCartWatchXHR = true;
    var reX = /\/cart\/(add|change|update|clear)(\.js)?/;
    var openOrig = XMLHttpRequest.prototype.open;
    var sendOrig = XMLHttpRequest.prototype.send;
    XMLHttpRequest.prototype.open = function (method, url) {
      try { this.__bbUrl = url || ''; } catch (e) {}
      return openOrig.apply(this, arguments);
    };
    XMLHttpRequest.prototype.send = function () {
      try {
        if (reX.test(this.__bbUrl || '')) {
          this.addEventListener('loadend', function () {
            clearTimeout(window.__bbCartWatchT);
            window.__bbCartWatchT = setTimeout(function () {
              registry.forEach(function (fn) { fn(); });
            }, 250);
          });
        }
      } catch (e) {}
      return sendOrig.apply(this, arguments);
    };
  })();

  function initBundleBuilder(root) {
    if (root.hasAttribute('data-bb-init')) return;
    root.setAttribute('data-bb-init', '');
    try { console.log('[3DPRO bundle] build 2026-10-20 (thumbnails, typeable qty, upgrades) loaded'); } catch (e) {}

    var d = root.dataset;
    var T2Q = +d.t2Qty, T2P = +d.t2Pct, T3Q = +d.t3Qty, T3P = +d.t3Pct;
    var UI_KEY = '3dpro-bundle-ui-' + (d.sectionId || 'default');
    var isMobile = function () { return !window.matchMedia('(min-width: 990px)').matches; };

    /* ---------- Money ---------- */
    var moneyFormat = d.money || '${{amount}}';
    var money = function (cents) {
      var amount = (cents / 100).toFixed(2);
      return moneyFormat.replace(/\{\{\s*amount[^}]*\}\}/, amount);
    };
    /* "$47" instead of "$47.00" for whole-dollar amounts (messages only) */
    var moneyShort = function (cents) { return money(cents).replace(/\.00(?!\d)/, ''); };
    var esc = function (s) { return String(s).replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;'); };
    /* Free-shipping threshold is set in the store's base currency (AUD).
       Shopify Markets may show prices in another currency (e.g. PHP), so
       convert with the storefront rate Shopify exposes on every page. */
    var fxRate = function () {
      var r = window.Shopify && window.Shopify.currency && parseFloat(window.Shopify.currency.rate);
      return r > 0 ? r : 1;
    };

    /* ---------- State ----------
       items are local until committed; `dirty` marks uncommitted changes.
       addons = cart lines that are NOT buckets and NOT the gift. They ride
       along in the summary (counted in the subtotal, NOT in the bucket count
       used for the discount tiers) and are never modified unless the shopper
       edits them here. */
    var state = { items: [], addons: [], collapsed: false, dirty: false };
    var giftInCart = false;
    try {
      var saved = JSON.parse(sessionStorage.getItem(UI_KEY));
      if (saved) {
        state.collapsed = !!saved.collapsed;
        if (saved.dirty && Array.isArray(saved.items)) {
          state.items = saved.items;
          if (Array.isArray(saved.addons)) state.addons = saved.addons;
          state.dirty = true;
        }
      }
    } catch (e) {}
    /* Every add-on variant we've ever seen this session — so a removal can be
       written to the cart as qty 0 (add-ons aren't in the fixed catalog). */
    var addonSeen = {};
    try {
      if (saved && Array.isArray(saved.addons)) {
        saved.addons.forEach(function (a) { addonSeen[String(a.variantId)] = true; });
      }
    } catch (e) {}
    var persist = function () {
      try {
        sessionStorage.setItem(UI_KEY, JSON.stringify({ items: state.items, addons: state.addons, dirty: state.dirty, collapsed: state.collapsed }));
      } catch (e) {}
    };

    var priceOf = function (it) {
      var c = catalog[String(it.variantId)];
      return c ? c.price : 0;
    };
    var derive = function () {
      /* Only buckets drive the count / tier / discount. Add-ons never do. */
      var count = state.items.reduce(function (n, it) { return n + it.qty; }, 0);
      var tier = count >= T3Q ? 3 : count >= T2Q ? 2 : 1;
      var pct = tier === 3 ? T3P : tier === 2 ? T2P : 0;
      var bucketSub = state.items.reduce(function (n, it) { return n + priceOf(it) * it.qty; }, 0);
      var bucketDisc = state.items.reduce(function (n, it) {
        return n + Math.round(priceOf(it) * (100 - pct) / 100) * it.qty;
      }, 0);
      /* Add-ons are charged at full price — they add to the subtotal/total
         (and so to the free-shipping order value) but get no bundle discount. */
      var addonSub = state.addons.reduce(function (n, a) { return n + (a.price || 0) * a.qty; }, 0);
      var subtotal = bucketSub + addonSub;
      var total = bucketDisc + addonSub;
      return { count: count, tier: tier, pct: pct, subtotal: subtotal, total: total, savings: bucketSub - bucketDisc };
    };

    /* ---------- UI refs (rail + drawer share data-* hooks) ---------- */
    var $$ = function (sel, ctx) { return Array.prototype.slice.call((ctx || root).querySelectorAll(sel)); };
    var drawer = root.querySelector('[data-drawer]');

    /* ---------- Catalog: which variants belong to this bundle ---------- */
    var catalog = {};
    $$('[data-card-data]').forEach(function (el) {
      try {
        var cd = JSON.parse(el.textContent);
        cd.variants.forEach(function (v) {
          catalog[String(v.id)] = { title: cd.title, price: cd.price, compareAt: cd.compareAt, colour: v.colour, image: v.image };
        });
      } catch (e) {}
    });

    /* ---------- Cart bridge ---------- */
    var cartFetch = function (url, payload) {
      var opts = { headers: { 'Accept': 'application/json' } };
      if (payload) {
        opts.method = 'POST';
        opts.headers['Content-Type'] = 'application/json';
        opts.headers['X-3DPRO-Bundle-Sync'] = '1';
        opts.body = JSON.stringify(payload);
      }
      return fetch(url, opts).then(function (r) {
        if (!r.ok) throw new Error('cart request failed: ' + url);
        return r.json();
      });
    };

    var hydrate = function (cart) {
      var items = [];
      var addons = [];
      var giftLine = null;
      giftInCart = false;
      (cart.items || []).forEach(function (line) {
        var vid = String(line.variant_id);
        if (d.giftVariant && vid === String(d.giftVariant)) { giftInCart = true; giftLine = line; return; }
        if (catalog[vid]) {
          items.push({ key: line.key, variantId: line.variant_id, qty: line.quantity });
          return;
        }
        /* Anything else in the cart is an add-on: carry its own display data
           (cart lines aren't in our catalog) so it can show under the divider. */
        var vt = (line.variant_title && line.variant_title !== 'Default Title') ? line.variant_title : '';
        var unit = (line.final_price != null ? line.final_price : line.price) || 0;
        var orig = line.original_price || 0;
        addonSeen[vid] = true;
        addons.push({
          key: line.key,
          variantId: line.variant_id,
          qty: line.quantity,
          title: line.product_title || line.title || '',
          colour: vt,
          price: unit,
          compareAt: orig > unit ? orig : 0,
          image: line.image || (line.featured_image && line.featured_image.url) || ''
        });
      });
      /* The gift product can also be sold as an Upgrade add-on (e.g. the
         shoulder strap). Anything beyond the one free tier-3 gift is the
         shopper's paid add-on, so show it as one. */
      if (giftLine) {
        var bucketCount = items.reduce(function (n, it) { return n + it.qty; }, 0);
        var extra = giftLine.quantity - (bucketCount >= T3Q ? 1 : 0);
        if (extra > 0) {
          addonSeen[String(giftLine.variant_id)] = true;
          addons.push({
            key: giftLine.key,
            variantId: giftLine.variant_id,
            qty: extra,
            title: giftLine.product_title || giftLine.title || '',
            colour: '',
            price: giftLine.original_price || giftLine.price || 0,
            compareAt: 0,
            image: giftLine.image || ''
          });
        }
      }
      state.items = items;
      state.addons = addons;
    };

    var refresh = function () {
      return cartFetch('/cart.js').then(function (cart) {
        hydrate(cart);
        state.dirty = false;
        render();
      });
    };
    /* re-hydrate on outside cart changes ONLY when the shopper has no
       uncommitted bundle edits — their in-progress bundle always wins */
    var refreshIfClean = function () {
      /* never re-render under a shopper who is mid-typing a quantity */
      var ae = document.activeElement;
      if (ae && ae.matches && ae.matches('[data-line-qty]') && root.contains(ae)) return;
      if (!state.dirty) refresh().catch(function () {});
    };
    registry.push(refreshIfClean);

    /* The theme's slider cart must NOT pop open when buckets are added
       here — it should only appear via the header cart icon or View cart.
       Some themes/apps auto-open it on any ajax add, so for a short window
       after each bundle write we force it shut. (Dawn: <cart-drawer>.) */
    /* Candidates for theme/app cart drawers. OCU (Zipify One Click Upsell)
       Slide Cart Drawer is targeted explicitly since it's installed here. */
    var DRAWER_CANDIDATES = 'cart-drawer, #CartDrawer, .cart-drawer, .drawer--cart, [data-cart-drawer], ' +
      '.ocu-cart-drawer, [id*="ocu-slide"], [class*="ocu-slide"], [id^="ocu"][class*="cart"], ' +
      '.rebuy-cart__flyout, #upCart, .upcart-container, #slidecart, .slidecarthq, #monster-cart';
    var OPENISH = ['is-open', 'open', 'opened', 'active', 'is-visible', 'is-active', 'drawer--is-open', 'cart-open', 'show', 'visible'];
    var looksOpen = function (el) {
      return OPENISH.some(function (c) { return el.classList.contains(c); }) || el.hasAttribute('open');
    };
    /* Close ANYTHING that looks like an open theme/app cart drawer.
       Never touches our own UI (guarded by the [data-bb] check). */
    var forceCloseThemeCarts = function () {
      document.querySelectorAll('cart-drawer').forEach(function (cd) {
        if (cd.classList.contains('active') || cd.classList.contains('animate')) {
          try { if (typeof cd.close === 'function') cd.close(); } catch (e) {}
          cd.classList.remove('active', 'animate');
        }
      });
      document.querySelectorAll(DRAWER_CANDIDATES).forEach(function (el) {
        if (el.closest('[data-bb]')) return;
        if (looksOpen(el)) {
          /* first choice: press the drawer's own close button so the app's
             internal state stays consistent (this is what closes OCU) */
          var closeBtn = el.querySelector('[class*="close"], [aria-label*="lose"], [data-close]');
          if (closeBtn) { try { closeBtn.click(); } catch (e) {} }
        }
        OPENISH.forEach(function (c) { el.classList.remove(c); });
        if (el.tagName === 'DETAILS') el.removeAttribute('open');
      });
      document.body.classList.remove('overflow-hidden', 'js-drawer-open', 'drawer-open', 'no-scroll', 'cart-open');
      document.documentElement.classList.remove('lock-scroll', 'overflow-hidden');
      /* only restore body scroll if OUR modal isn't the one locking it */
      if (!root.querySelector('dialog[open]') && document.body.style.overflow === 'hidden') {
        document.body.style.overflow = '';
      }
    };
    /* For ~1.6s after each bundle write, shut the theme drawer the instant
       anything opens it (apps often open it async after their own fetch). */
    var suppressUntil = 0;
    var suppressObserver = null;
    var suppressExtensions = 0;
    var endSuppression = function () {
      /* if a drawer still claims to be open, extend and keep trying to close
         it (max 3 extensions), otherwise fail open so we never trap the UI */
      var stillOpen = Array.prototype.some.call(
        document.querySelectorAll(DRAWER_CANDIDATES),
        function (el) { return !el.closest('[data-bb]') && looksOpen(el); }
      );
      if (stillOpen && suppressExtensions < 3) {
        suppressExtensions++;
        suppressUntil = Date.now() + 1200;
        forceCloseThemeCarts();
        setTimeout(endSuppression, 1200);
        return;
      }
      suppressExtensions = 0;
      document.documentElement.classList.remove('bb-cart-suppress');
      try { if (suppressObserver) suppressObserver.disconnect(); } catch (e) {}
    };
    var keepThemeDrawerClosed = function () {
      suppressUntil = Date.now() + 1600;
      /* CSS shield: while this class is on <html>, drawer candidates are
         invisible (see bundle-builder.css), so nothing can flash open */
      document.documentElement.classList.add('bb-cart-suppress');
      forceCloseThemeCarts();
      [120, 320, 650, 1000, 1500].forEach(function (ms) {
        setTimeout(function () { if (Date.now() <= suppressUntil + 100) forceCloseThemeCarts(); }, ms);
      });
      try {
        if (!suppressObserver) {
          suppressObserver = new MutationObserver(function () {
            if (Date.now() > suppressUntil) return;
            forceCloseThemeCarts();
          });
        }
        suppressObserver.observe(document.body, { attributes: true, childList: true, subtree: true, attributeFilter: ['class', 'open', 'style'] });
      } catch (e) {}
      setTimeout(endSuppression, 1650);
    };

    /* View cart / header icon = the ONLY ways the slider cart opens.
       Clicking the real header icon triggers whatever the theme has bound
       to it (slider cart on Dawn); themes without a slider just navigate. */
    var openThemeCart = function () {
      var icon = document.getElementById('cart-icon-bubble')
        || document.querySelector('a[href$="/cart"], a[href*="/cart?"]');
      if (icon) { icon.click(); return; }
      window.location.href = '/cart';
    };

    /* Serial write queue: optimistic UI first, cart writes one at a time.
       Any failure re-hydrates from the cart so UI snaps back to the truth. */
    var chain = Promise.resolve();
    var queue = function (fn) {
      chain = chain.then(fn).catch(function () {
        alert('Something went wrong saving your bundle to the cart — one item may be out of stock. Please try again.');
        /* keep local bundle (still dirty) so the shopper can just retry */
      });
      return chain;
    };


    /* Best-effort: keep the theme's header cart count AND slider-cart
       contents in sync with our writes (Dawn section rendering API) */
    var syncThemeCart = function () {
      var icon = document.getElementById('cart-icon-bubble');
      var themeDrawer = document.querySelector('cart-drawer');
      if (!icon && !themeDrawer) return Promise.resolve();
      var sections = [];
      if (icon) sections.push('cart-icon-bubble');
      if (themeDrawer) sections.push('cart-drawer');
      var base = (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || '/';
      return fetch(base + '?sections=' + sections.join(','))
        .then(function (r) { return r.json(); })
        .then(function (s) {
          if (!s) return;
          if (icon && s['cart-icon-bubble']) {
            var t1 = document.createElement('div');
            t1.innerHTML = s['cart-icon-bubble'];
            var freshIcon = t1.querySelector('#cart-icon-bubble');
            icon.innerHTML = freshIcon ? freshIcon.innerHTML : t1.innerHTML;
          }
          if (themeDrawer && s['cart-drawer']) {
            var t2 = document.createElement('div');
            t2.innerHTML = s['cart-drawer'];
            var freshDrawer = t2.querySelector('cart-drawer');
            if (freshDrawer) {
              themeDrawer.innerHTML = freshDrawer.innerHTML;
              themeDrawer.classList.toggle('is-empty', freshDrawer.classList.contains('is-empty'));
              /* re-bind the overlay click Dawn attaches on load */
              var ov = themeDrawer.querySelector('#CartDrawer-Overlay');
              if (ov) ov.addEventListener('click', function () {
                if (typeof themeDrawer.close === 'function') themeDrawer.close();
                else themeDrawer.classList.remove('active', 'animate');
              });
            }
          }
        })
        .catch(function () {});
    };

    /* ---------- Line rows ---------- */
    var displayItem = function (it) {
      var cat = catalog[String(it.variantId)] || {};
      return {
        key: it.key, variantId: it.variantId, qty: it.qty, gift: it.gift,
        title: cat.title || it.title || '', price: cat.price || 0, compareAt: cat.compareAt || 0,
        colour: cat.colour, image: cat.image
      };
    };

    var lineRow = function (it, pct, isAddon) {
      var li = document.createElement('li');
      li.className = 'bb__item' + (it.gift ? ' bb__gift' : '') + (isAddon ? ' bb__item--addon' : '');
      var eff = pct ? Math.round(it.price * (100 - pct) / 100) : it.price;
      var strike = (it.compareAt && it.compareAt > it.price) ? it.compareAt : (pct ? it.price : 0);
      var priced = it.gift
        ? '<span class="bb__itemprice">' + (it.compareAt > 0 ? '<s>' + money(it.compareAt) + '</s>' : '') + 'FREE</span>'
        : strike
          ? '<span class="bb__itemprice"><s>' + money(strike) + '</s>' + money(eff) + '</span>'
          : '<span class="bb__itemprice">' + money(eff) + '</span>';
      var qty = it.gift
        ? '<span class="bb__qtyval">1</span>'
        : '<span class="bb__qty">' +
          '<button type="button" class="bb__qtybtn" data-dec aria-label="Decrease quantity">&minus;</button>' +
          '<input type="number" class="bb__qtyval bb__qtyinput" data-line-qty value="' + it.qty + '"' +
          ' min="0" max="99" step="1" inputmode="numeric" pattern="[0-9]*" aria-label="Quantity">' +
          '<button type="button" class="bb__qtybtn" data-inc aria-label="Increase quantity">+</button>' +
          '</span>';
      li.innerHTML =
        (it.image ? '<img class="bb__thumb" src="' + it.image + '" alt="" loading="lazy" width="40" height="40">' : '<span class="bb__thumb"></span>') +
        '<span class="bb__itemname">' + it.title + (it.colour ? '<small>' + it.colour + '</small>' : '') + '</span>' +
        qty + priced;
      /* Direct listeners: these survive any theme script that intercepts bubbling clicks */
      var decBtn = li.querySelector('[data-dec]');
      var incBtn = li.querySelector('[data-inc]');
      if (decBtn) decBtn.addEventListener('click', function (ev) {
        ev.preventDefault(); ev.stopPropagation(); suppressCollapse = Date.now();
        (isAddon ? changeAddonQty : changeQty)(it.variantId, -1);
      });
      /* Typed quantity (panel + bottom cart): applied on Enter / leaving the
         field. 0 removes the line; blank or invalid snaps back. */
      var qtyIn = li.querySelector('[data-line-qty]');
      if (qtyIn) {
        var applyTyped = function () {
          var raw = String(qtyIn.value).trim();
          var n = parseInt(raw, 10);
          if (raw === '' || isNaN(n) || n < 0) { qtyIn.value = it.qty; return; }
          n = Math.min(99, n);
          if (n === it.qty) { qtyIn.value = n; return; }
          suppressCollapse = Date.now();
          (isAddon ? changeAddonQty : changeQty)(it.variantId, n - it.qty);
        };
        qtyIn.addEventListener('click', function (ev) { ev.stopPropagation(); suppressCollapse = Date.now(); });
        qtyIn.addEventListener('focus', function () { setTimeout(function () { try { qtyIn.select(); } catch (er) {} }, 0); });
        qtyIn.addEventListener('input', function () {
          var digits = String(qtyIn.value).replace(/\D/g, '').slice(0, 2);
          if (digits !== String(qtyIn.value)) qtyIn.value = digits;
        });
        qtyIn.addEventListener('change', applyTyped);
        qtyIn.addEventListener('keydown', function (ev) {
          if (ev.key === 'Enter') { ev.preventDefault(); qtyIn.blur(); }
          else if (ev.key === 'Escape') { qtyIn.value = it.qty; qtyIn.blur(); }
        });
      }
      if (incBtn) incBtn.addEventListener('click', function (ev) {
        ev.preventDefault(); ev.stopPropagation(); suppressCollapse = Date.now();
        (isAddon ? changeAddonQty : changeQty)(it.variantId, 1);
      });
      return li;
    };

    var render = function () {
      var dv = derive();
      var remaining2 = Math.max(0, T2Q - dv.count);
      var remaining3 = Math.max(0, T3Q - dv.count);
      var msg = dv.tier === 3 ? d.msg3 : dv.tier === 2 ? d.msg2.replace('{count}', remaining3) : d.msg1.replace('{count}', remaining2);

      $$('[data-count]').forEach(function (el) { el.textContent = dv.count; });
      $$('[data-bar]').forEach(function (el) { el.style.width = (Math.min(dv.count / T3Q, 1) * 100) + '%'; });
      $$('[data-dot2]').forEach(function (el) { el.classList.toggle('is-hit', dv.count >= T2Q); });
      $$('[data-dot3]').forEach(function (el) { el.classList.toggle('is-hit', dv.count >= T3Q); });
      $$('[data-tier2-label]').forEach(function (el) { el.classList.toggle('is-unlocked', dv.tier >= 2); });
      $$('[data-tier3-label]').forEach(function (el) { el.classList.toggle('is-unlocked', dv.tier >= 3); });
      $$('[data-msg]').forEach(function (el) {
        var safe = String(msg).replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;');
        el.innerHTML = safe.replace(/(\d+%\s*OFF)/gi, '<span class="bb__hl">$1</span>');
      });
      var hasAnything = dv.count > 0 || state.addons.length > 0;
      $$('[data-subtotal]').forEach(function (el) { el.textContent = hasAnything ? money(dv.subtotal) : '—'; });
      $$('[data-total]').forEach(function (el) { el.textContent = hasAnything ? money(dv.total) : '—'; });
      /* Free shipping is earned by order value only — never by tier. dv.total
         is the WHOLE cart (discounted buckets + every add-on / non-bucket
         product; the gift is free). The Shipping row stays hidden until the
         threshold is reached.
         NOTE: data-freeship-threshold → dataset.freeshipThreshold (lowercase
         "s" — the old d.freeShipThreshold was always undefined). */
      var shipTarget = Math.round((+d.freeshipThreshold || 0) * fxRate());
      var shipLeft = Math.max(0, shipTarget - dv.total);
      var qualifies = shipTarget > 0 && shipLeft === 0;
      $$('[data-ship-row]').forEach(function (row) { row.hidden = !qualifies; });
      /* Free shipping line above the bar: "Add $47 to unlock FREE Shipping",
         amount + "FREE Shipping" highlighted (Figma 1319:67). */
      var shipHtml = '';
      if (shipTarget > 0) {
        shipHtml = qualifies
          ? esc(d.freeshipDone || '🎉 You unlocked FREE Shipping!')
          : esc(d.freeshipMsg || 'Add {amount} to unlock FREE Shipping').replace('{amount}', function () {
              return '<span class="bb__hl">' + moneyShort(shipLeft) + '</span>';
            });
        shipHtml = shipHtml.replace(/(FREE\s+Shipping)/i, '<span class="bb__hl">$1</span>');
      }
      $$('[data-shipmsg]').forEach(function (el) { el.innerHTML = shipHtml; });
      $$('[data-shipping]').forEach(function (el) { el.textContent = 'FREE'; });
      $$('[data-save-row]').forEach(function (el) { el.hidden = dv.savings <= 0; });
      $$('[data-savings]').forEach(function (el) { el.textContent = '−' + money(dv.savings); });
      /* Checkout is live whenever there's anything to buy — buckets OR add-ons. */
      $$('[data-checkout], [data-addtocart]').forEach(function (el) { el.disabled = !hasAnything; });

      $$('[data-items]').forEach(function (list) {
        list.textContent = '';
        state.items.forEach(function (it) { list.appendChild(lineRow(displayItem(it), dv.pct)); });
        if (dv.tier === 3 && d.giftVariant) {
          list.appendChild(lineRow({
            gift: true,
            title: d.giftLabel,
            price: 0,
            compareAt: d.giftPrice,   /* struck-through original price */
            image: d.giftImage,
            qty: 1
          }, 0));
        }
        /* Add-ons sit below the buckets/gift, separated by a subtle divider.
           They're full price and editable, but don't touch the tier progress. */
        if (state.addons.length) {
          var divider = document.createElement('li');
          divider.className = 'bb__divider';
          divider.setAttribute('aria-hidden', 'true');
          list.appendChild(divider);
          state.addons.forEach(function (a) { list.appendChild(lineRow(a, 0, true)); });
        }
      });

      if (drawer) {
        var titleEl = drawer.querySelector('[data-drawer-title]');
        /* With buckets, use the {count} template; add-ons only → neutral title. */
        if (titleEl) titleEl.textContent = dv.count > 0
          ? (d.drawerTitle || '{count} in Your Bundle').replace('{count}', dv.count)
          : 'Your Bundle';
        /* Show the drawer for buckets OR add-ons, so add-ons in the cart are
           always visible in the bottom drawer even with no buckets yet. */
        if (hasAnything) {
          drawer.hidden = false;
          requestAnimationFrame(function () { drawer.classList.add('is-shown'); });
        } else {
          drawer.classList.remove('is-shown');
        }
        var wasCollapsed = drawer.classList.contains('is-collapsed');
        drawer.classList.toggle('is-collapsed', !!state.collapsed);
        drawer.classList.toggle('is-expanded', !!state.expanded && !state.collapsed);
        /* when the drawer opens/expands, always show it from the TOP */
        if (wasCollapsed && !state.collapsed) {
          var body = drawer.querySelector('.bb__drawerbody');
          if (body) requestAnimationFrame(function () { body.scrollTop = 0; });
        }
        var toggle = drawer.querySelector('[data-drawer-toggle]');
        if (toggle) toggle.setAttribute('aria-expanded', String(!state.collapsed));
      }
      persist();
    };

    /* ---------- Bundle mutations (optimistic + queued cart write) ---------- */
    /* All bundle edits are LOCAL — no cart traffic, nothing for cart apps
       to react to. The cart is only written on View cart / Checkout. */
    /* ---------- Live cart push ----------
       Every mutation writes to the real cart immediately. The theme slider is
       kept suppressed so it never flings open on a bucket change; we only sync
       its contents/count silently. Debounced + queued so rapid clicks can't
       race the Cart API. */
    var pushTimer = null;
    var pushToCart = function () {
      state.dirty = true;      /* pending write — our state wins over foreign refreshes */
      persist();
      keepThemeDrawerClosed();  /* hold the slider shut across the write */
      if (pushTimer) clearTimeout(pushTimer);
      pushTimer = setTimeout(function () {
        queue(function () {
          return commitBundle()      /* atomic /cart/update.js with absolute qtys + gift-by-tier */
            .then(syncThemeCart)     /* refresh header count + slider contents, silently */
            .then(function () { state.dirty = false; persist(); });
        });
      }, 260);
    };

    var addItem = function (variantId, qty) {
      qty = Math.max(1, parseInt(qty, 10) || 1);
      var found = state.items.find(function (it) { return String(it.variantId) === String(variantId); });
      if (found) found.qty += qty;
      else state.items.push({ variantId: variantId, qty: qty });
      state.collapsed = false; /* every add opens our drawer */
      render();                /* optimistic UI immediately */
      pushToCart();            /* ...then persist to the real cart */
    };

    var changeQty = function (variantId, delta) {
      var i = state.items.findIndex(function (it) { return String(it.variantId) === String(variantId); });
      if (i < 0) return;
      state.items[i].qty += delta;
      if (state.items[i].qty <= 0) state.items.splice(i, 1);
      render();
      pushToCart();
    };

    /* Add-ons live outside the bucket count, so they get their own stepper
       handler. Same optimistic-then-commit flow as buckets. */
    var changeAddonQty = function (variantId, delta) {
      var i = state.addons.findIndex(function (a) { return String(a.variantId) === String(variantId); });
      if (i < 0) return;
      state.addons[i].qty += delta;
      if (state.addons[i].qty <= 0) state.addons.splice(i, 1);
      render();
      pushToCart();
    };

    /* Upgrade blocks: add a product at full price as an add-on (under the
       divider in Your Bundle, never counted toward the bucket tiers). */
    var addAddon = function (a) {
      var found = state.addons.find(function (x) { return String(x.variantId) === String(a.variantId); });
      if (found) found.qty += 1;
      else state.addons.push({ variantId: a.variantId, qty: 1, title: a.title, colour: '', price: a.price, compareAt: a.compareAt > a.price ? a.compareAt : 0, image: a.image });
      addonSeen[String(a.variantId)] = true;
      state.collapsed = false;
      render();
      pushToCart();
    };

    var clearBundle = function () {
      state.items = [];
      render();
      pushToCart();
    };

    /* Commit the bundle to the real cart: ONE /cart/update.js call with
       absolute quantities for every catalog variant (+ gift by tier).
       update.js adds missing lines, changes existing ones and removes
       zeroed ones in a single atomic request. */
    var commitBundle = function () {
      var updates = {};
      Object.keys(catalog).forEach(function (vid) { updates[vid] = 0; });
      /* Zero every add-on we've seen, then set the ones still present — so a
         removed add-on is written back to the cart as 0, just like buckets. */
      Object.keys(addonSeen).forEach(function (vid) { updates[vid] = 0; });
      state.items.forEach(function (it) { updates[String(it.variantId)] = it.qty; });
      if (d.giftVariant) updates[String(d.giftVariant)] = derive().tier === 3 ? 1 : 0;
      /* Write add-on quantities too, so editing/removing one here sticks.
         If an add-on is the gift variant, it stacks on top of the free one. */
      state.addons.forEach(function (a) {
        var k = String(a.variantId);
        updates[k] = a.qty + (d.giftVariant && k === String(d.giftVariant) ? updates[k] : 0);
      });
      return cartFetch('/cart/update.js', { updates: updates }).then(function () {
        state.dirty = false;
        persist();
      });
    };

    var anyThemeDrawerOpen = function () {
      return Array.prototype.some.call(
        document.querySelectorAll(DRAWER_CANDIDATES),
        function (el) { return !el.closest('[data-bb]') && looksOpen(el); }
      );
    };

    /* Cart is always live now, so there's nothing to commit first. */
    var goToCart = function () {
      /* flush any pending debounced write, then open the theme slider */
      if (pushTimer) { clearTimeout(pushTimer); pushTimer = null; }
      queue(function () {
        return commitBundle().then(syncThemeCart).then(function () {
          state.dirty = false; persist();
          setTimeout(function () { openThemeCart(); }, 60);
        });
      });
    };

    var goToCheckout = function () {
      if (pushTimer) { clearTimeout(pushTimer); pushTimer = null; }
      keepThemeDrawerClosed();
      queue(function () {
        return commitBundle().then(function () {
          window.location.href = '/checkout';
        });
      });
    };

    /* ---------- Modal manager (native <dialog>) ---------- */
    var openDialog = null;
    var Modal = {
      open: function (name) {
        Modal.close();
        var dlg = root.querySelector('[data-modal="' + name + '"]');
        if (!dlg) return null;
        dlg.showModal();
        document.body.style.overflow = 'hidden';
        openDialog = dlg;
        return dlg;
      },
      close: function () {
        if (!openDialog) return;
        openDialog.close();
        document.body.style.overflow = '';
        openDialog = null;
      }
    };
    $$('dialog[data-modal]').forEach(function (dlg) {
      dlg.addEventListener('click', function (e) { if (e.target === dlg) Modal.close(); });
      dlg.addEventListener('close', function () { document.body.style.overflow = ''; if (openDialog === dlg) openDialog = null; });
    });

    /* ---------- Product modal helpers ---------- */
    var initProductModal = function (content) {
      /* stop any video when the popup is closed */
      var dlgHost = content.closest('dialog');
      if (dlgHost && !dlgHost.hasAttribute('data-vid-bound')) {
        dlgHost.setAttribute('data-vid-bound', '');
        dlgHost.addEventListener('close', function () {
          dlgHost.querySelectorAll('video').forEach(function (v) { try { v.pause(); } catch (e) {} });
        });
      }
      var track = content.querySelector('[data-gal-track]');
      var thumbs = $$('[data-gal-thumb]', content);
      var slideW = function () { return track ? track.clientWidth : 0; };
      var goToSlide = function (i) { if (track) track.scrollTo({ left: i * slideW(), behavior: 'smooth' }); };
      thumbs.forEach(function (th) {
        th.addEventListener('click', function () { goToSlide(+th.dataset.galThumb); });
      });
      /* Play the video on the visible slide (muted autoplay), pause the rest.
         Browsers only allow autoplay when muted — that's a platform rule. */
      var syncVideos = function (i) {
        var slides = $$('[data-gal-slide]', content);
        slides.forEach(function (el, j) {
          if (el.tagName !== 'VIDEO') return;
          if (j === i) {
            if (el.hasAttribute('data-autoplay')) {
              el.muted = true; /* required for autoplay to be allowed */
              var pr = el.play();
              if (pr && pr.catch) pr.catch(function () { /* blocked: leave paused */ });
            }
          } else if (!el.paused) {
            try { el.pause(); } catch (e) {}
          }
        });
      };
      var pauseVideosExcept = syncVideos; /* keeps existing call sites working */
      if (track) {
        var raf;
        track.addEventListener('scroll', function () {
          cancelAnimationFrame(raf);
          raf = requestAnimationFrame(function () {
            var i = Math.round(track.scrollLeft / (slideW() || 1));
            thumbs.forEach(function (th, j) { th.classList.toggle('is-active', j === i); });
            pauseVideosExcept(i);
          });
        }, { passive: true });
      }
      /* start the first slide's video when the popup opens */
      setTimeout(function () { syncVideos(0); }, 60);

      var prev = content.querySelector('[data-gal-prev]');
      var next = content.querySelector('[data-gal-next]');
      if (prev) prev.addEventListener('click', function () { track.scrollBy({ left: -slideW(), behavior: 'smooth' }); });
      if (next) next.addEventListener('click', function () { track.scrollBy({ left: slideW(), behavior: 'smooth' }); });

      var tabs = $$('[data-tab]', content);
      var panels = $$('[data-panel-i]', content);
      var seg = $$('[data-seg] span', content);
      var curTab = 0;
      var tabCount = tabs.length;
      var selectTab = function (i) {
        if (i < 0 || i >= tabCount) return;
        curTab = i;
        tabs.forEach(function (b) { b.setAttribute('aria-selected', String(+b.dataset.tab === i)); });
        panels.forEach(function (p) { p.hidden = +p.dataset.panelI !== i; });
        seg.forEach(function (sp, j) { sp.classList.toggle('is-active', j === i); });
      };
      tabs.forEach(function (btn) {
        btn.addEventListener('click', function () { selectTab(+btn.dataset.tab); });
      });

      /* Swipe left/right on the panels to move between tabs */
      var panelsWrap = content.querySelector('[data-tabs]') || content;
      var sx = null, sy = null;
      panelsWrap.addEventListener('touchstart', function (e) {
        var t0 = e.touches && e.touches[0];
        if (t0) { sx = t0.clientX; sy = t0.clientY; }
      }, { passive: true });
      panelsWrap.addEventListener('touchend', function (e) {
        if (sx === null) return;
        var t1 = e.changedTouches && e.changedTouches[0];
        if (!t1) { sx = null; return; }
        var dx = t1.clientX - sx, dy = t1.clientY - sy;
        sx = null; sy = null;
        /* horizontal swipe only (ignore vertical scrolls) */
        if (Math.abs(dx) < 45 || Math.abs(dx) < Math.abs(dy)) return;
        selectTab(dx < 0 ? curTab + 1 : curTab - 1);
      });

      var jump = content.querySelector('[data-review-jump]');
      if (jump) jump.addEventListener('click', function () {
        Modal.close();
        var sel = jump.getAttribute('data-review-anchor') || d.reviewsAnchor;
        var target = sel && (document.querySelector(sel) || document.getElementById((sel || '').replace(/^#/, '')));
        if (target) target.scrollIntoView({ behavior: 'smooth' });
      });
    };

    /* ---------- STOQ (Back in Stock) ---------- */
    var whenStoqReady = function (fn) {
      var ready = function () {
        return window._RestockRocket && typeof window._RestockRocket.openModal === 'function';
      };
      if (ready()) { fn(); return; }
      var done = false;
      var run = function () { if (!done && ready()) { done = true; fn(); } };
      /* STOQ fires this when its script initialises on the page */
      window.addEventListener('stoq:loaded', run, { once: true });
      var tries = 0;
      (function poll() {
        if (done) return;
        if (ready()) { run(); return; }
        if (tries++ < 25) setTimeout(poll, 200);
        else {
          console.warn('3DPRO bundle: STOQ (window._RestockRocket) is not available on this page. Check Theme editor → App embeds → STOQ is ON, and that the Notify Me widget is enabled in the STOQ dashboard.');
          alert('Sorry, the back-in-stock signup could not load. Please try again in a moment.');
        }
      })();
    };
    var updateCardButtons = function (card, available) {
      var add = card.querySelector('[data-add]');
      var notify = card.querySelector('[data-notify]');
      if (add) add.hidden = !available;
      if (notify) notify.hidden = !!available;
    };

    /* ---------- Product popup ---------- */
    var openProduct = function (tplId) {
      pauseCardVideos();
      var dlg = Modal.open('product');
      if (!dlg) return;
      var slot = dlg.querySelector('[data-modal-content]');
      var tpl = document.getElementById(tplId);
      slot.textContent = '';
      if (tpl) {
        slot.appendChild(tpl.content.cloneNode(true));
        initProductModal(slot);
      }
    };

    /* ---------- Card gallery (arrows + swipe) ----------
       Browse the same slides as the bucket's popup (card-data "gallery" +
       "galleryVideos"), starting from the selected colour's image. Video
       slides autoplay muted + loop while shown and pause when you move on;
       they only download when reached (preload="none"). The gallery only
       changes what's shown — the colour added to the bundle is always the
       selected swatch, and picking a swatch jumps back to its photo. */
    var cardGallery = function (card) {
      var data;
      try { data = JSON.parse(card.querySelector('[data-card-data]').textContent); } catch (e) { return []; }
      var v = data.variants.find(function (x) { return String(x.id) === String(data.selected); }) || data.variants[0] || {};
      var imgs = data.gallery || [], vids = data.galleryVideos || [];
      var list = [], seen = {};
      if (v.image) { list.push({ img: v.image, video: '' }); seen[v.image] = true; }
      imgs.forEach(function (u, i) {
        var img = u && u !== '-' ? u : '';
        var video = vids[i] && vids[i] !== '-' ? vids[i] : '';
        if (!img && !video) return;
        if (!video && seen[img]) return; /* same photo as the colour image */
        if (!video) seen[img] = true;
        list.push({ img: img, video: video });
      });
      return list;
    };
    var cardVideo = function (card, create) {
      var vid = card.querySelector('[data-card-video]');
      if (!vid && create) {
        vid = document.createElement('video');
        vid.className = 'bb__cardvideo';
        vid.setAttribute('data-card-video', '');
        vid.setAttribute('playsinline', '');
        vid.setAttribute('muted', '');
        vid.setAttribute('loop', '');
        vid.setAttribute('preload', 'none');
        vid.muted = true;
        var media = card.querySelector('.bb__cardmedia');
        var img0 = media && media.querySelector('img');
        if (img0) img0.insertAdjacentElement('afterend', vid);
        else if (media) media.insertBefore(vid, media.firstChild);
      }
      return vid;
    };
    var showCardSlide = function (card, slide) {
      var img = card.querySelector('[data-card-img]');
      if (slide.img && img) {
        img.removeAttribute('srcset');
        img.removeAttribute('sizes');
        img.src = slide.img;
      }
      if (slide.video) {
        var vid = cardVideo(card, true);
        if (vid.getAttribute('src') !== slide.video) {
          vid.setAttribute('src', slide.video);
          if (slide.img) vid.setAttribute('poster', slide.img); else vid.removeAttribute('poster');
        }
        vid.hidden = false;
        vid.muted = true; /* browsers only allow muted autoplay */
        var pr = vid.play();
        if (pr && pr.catch) pr.catch(function () {});
      } else {
        var old = cardVideo(card, false);
        if (old) { try { old.pause(); } catch (e) {} old.hidden = true; }
      }
    };
    /* ---------- Card thumbnails (Figma 1835:259) ----------
       Built from cardGallery() so each thumb's index matches the arrows.
       Thumbs request a small CDN rendition instead of the 500px slide. */
    var thumbUrl = function (u) {
      if (!u) return '';
      return /[?&]width=\d+/.test(u) ? u.replace(/([?&])width=\d+/, '$1width=120') : u;
    };
    var setActiveThumb = function (card, i) {
      $$('[data-card-thumb]', card).forEach(function (b) {
        var on = +b.dataset.cardThumb === i;
        b.classList.toggle('is-active', on);
        b.setAttribute('aria-current', on ? 'true' : 'false');
        if (on && b.parentNode.scrollWidth > b.parentNode.clientWidth) {
          b.parentNode.scrollTo({ left: b.offsetLeft - (b.parentNode.clientWidth - b.offsetWidth) / 2, behavior: 'smooth' });
        }
      });
    };
    var renderCardThumbs = function (card) {
      var wrap = card.querySelector('[data-card-thumbs]');
      if (!wrap) return;
      var list = cardGallery(card);
      wrap.textContent = '';
      wrap.hidden = list.length < 2;
      if (wrap.hidden) return;
      list.forEach(function (slide, i) {
        var b = document.createElement('button');
        b.type = 'button';
        b.className = 'bb__cardthumb';
        b.setAttribute('data-card-thumb', i);
        b.setAttribute('aria-label', (slide.video ? 'Video ' : 'Photo ') + (i + 1) + ' of ' + list.length);
        b.innerHTML = (slide.img ? '<img src="' + esc(thumbUrl(slide.img)) + '" alt="" loading="lazy" width="43" height="43">' : '') +
          (slide.video ? '<span class="bb__cardthumbplay" aria-hidden="true"><svg viewBox="0 0 24 24"><path d="M8 5v14l11-7z"/></svg></span>' : '');
        wrap.appendChild(b);
      });
      setActiveThumb(card, card.__galIdx || 0);
    };
    var goToCardSlide = function (card, i) {
      var list = cardGallery(card);
      if (!list[i]) return;
      card.__galIdx = i;
      showCardSlide(card, list[i]);
      setActiveThumb(card, i);
    };

    var stepCardGallery = function (card, dir) {
      var list = cardGallery(card);
      if (list.length < 2) return;
      var i = ((card.__galIdx || 0) + dir + list.length) % list.length;
      card.__galIdx = i;
      showCardSlide(card, list[i]);
      setActiveThumb(card, i);
      /* warm the next photo in the same direction (never pre-loads video) */
      var nx = list[(i + dir + list.length) % list.length];
      if (nx && nx.img && !nx.video) { var pre = new Image(); pre.src = nx.img; }
    };
    var pauseCardVideos = function () {
      $$('[data-card-video]').forEach(function (v) { try { v.pause(); } catch (e) {} });
    };
    $$('[data-card]').forEach(function (card) {
      renderCardThumbs(card);
      /* hide the arrows on cards with only one slide */
      if (cardGallery(card).length < 2) {
        $$('[data-card-prev], [data-card-next]', card).forEach(function (b) { b.hidden = true; });
        return;
      }
      /* swipe left/right on the card image (mobile). A swipe never opens the
         popup; a plain tap still does. */
      var media = card.querySelector('.bb__cardmedia');
      if (!media) return;
      var sx = null, sy = null;
      media.addEventListener('touchstart', function (ev) {
        var t0 = ev.touches && ev.touches[0];
        if (t0) { sx = t0.clientX; sy = t0.clientY; }
      }, { passive: true });
      media.addEventListener('touchend', function (ev) {
        if (sx === null) return;
        var t1 = ev.changedTouches && ev.changedTouches[0];
        var dx = t1 ? t1.clientX - sx : 0, dy = t1 ? t1.clientY - sy : 0;
        sx = null; sy = null;
        if (Math.abs(dx) < 35 || Math.abs(dx) < Math.abs(dy)) return;
        card.__swipedAt = Date.now();
        stepCardGallery(card, dx < 0 ? 1 : -1);
      });
    });

    /* ---------- Card quantity (typeable stepper) ----------
       The number between − and + is an <input>; shoppers can type any
       quantity 1–99. Blank / invalid snaps back to 1 when they leave it. */
    var QTY_MAX = 99;
    var clampQty = function (n) { return Math.min(QTY_MAX, Math.max(1, n || 1)); };
    var cardQty = function (el) { return clampQty(parseInt(el.value, 10)); };
    root.addEventListener('input', function (e) {
      var el = e.target.closest && e.target.closest('[data-card-qtyval]');
      if (!el) return;
      /* digits only, max 2 chars; allow empty while typing */
      var digits = String(el.value).replace(/\D/g, '').slice(0, 2);
      if (digits !== String(el.value)) el.value = digits;
    });
    root.addEventListener('change', function (e) {
      var el = e.target.closest && e.target.closest('[data-card-qtyval]');
      if (el) el.value = cardQty(el);
    });
    root.addEventListener('focusin', function (e) {
      var el = e.target.closest && e.target.closest('[data-card-qtyval]');
      if (el) setTimeout(function () { try { el.select(); } catch (er) {} }, 0);
    });
    root.addEventListener('keydown', function (e) {
      var el = e.target.closest && e.target.closest('[data-card-qtyval]');
      if (!el || e.key !== 'Enter') return;
      /* Enter = confirm the number and add to bundle */
      e.preventDefault();
      el.value = cardQty(el);
      var addBtn = el.closest('[data-card]').querySelector('[data-add]');
      if (addBtn && !addBtn.hidden) addBtn.click();
      el.blur();
    });

    /* ---------- Event delegation ---------- */
    root.addEventListener('click', function (e) {
      var t = e.target;

      var galBtn = t.closest('[data-card-prev], [data-card-next]');
      if (galBtn) {
        e.preventDefault();
        stepCardGallery(galBtn.closest('[data-card]'), galBtn.hasAttribute('data-card-next') ? 1 : -1);
        return;
      }

      var thumb = t.closest('[data-card-thumb]');
      if (thumb) {
        e.preventDefault();
        goToCardSlide(thumb.closest('[data-card]'), +thumb.dataset.cardThumb);
        return;
      }

      var upBtn = t.closest('[data-upgrade]');
      if (upBtn) {
        e.preventDefault();
        if (upBtn.disabled) return;
        addAddon({
          variantId: +upBtn.dataset.variantId,
          title: upBtn.dataset.title || '',
          price: +upBtn.dataset.price || 0,
          compareAt: +upBtn.dataset.compare || 0,
          image: upBtn.dataset.image || ''
        });
        upBtn.setAttribute('data-flash', '');
        setTimeout(function () { upBtn.removeAttribute('data-flash'); }, 600);
        return;
      }

      var opener = t.closest('[data-modal-open]');
      if (opener) {
        var name = opener.dataset.modalOpen;
        if (name === 'product') openProduct(opener.dataset.productTpl);
        else Modal.open(name);
        return;
      }
      if (t.closest('[data-modal-close]')) { Modal.close(); return; }

      var sw = t.closest('[data-swatch]');
      if (sw) {
        var card = sw.closest('[data-card]');
        var data = JSON.parse(card.querySelector('[data-card-data]').textContent);
        var v = data.variants.find(function (x) { return String(x.id) === sw.dataset.variantId; });
        if (v) {
          data.selected = v.id;
          card.querySelector('[data-card-data]').textContent = JSON.stringify(data);
          $$('[data-swatch]', card).forEach(function (b) { b.setAttribute('aria-pressed', String(b === sw)); });
          var img = card.querySelector('[data-card-img]');
          if (img && v.image) { img.src = v.image; img.removeAttribute('srcset'); }
          card.__galIdx = 0; /* arrows restart from this colour's photo */
          renderCardThumbs(card); /* first thumb is now this colour's photo */
          var cv = cardVideo(card, false);
          if (cv) { try { cv.pause(); } catch (e) {} cv.hidden = true; }
          var nameEl = card.querySelector('[data-swatch-name]');
          if (nameEl && v.colour) nameEl.textContent = v.colour;
          updateCardButtons(card, v.available !== false);
        }
        return;
      }

      var notifyBtn = t.closest('[data-notify]');
      if (notifyBtn) {
        var card3 = notifyBtn.closest('[data-card]');
        var pj = card3.querySelector('[data-stoq-product]');
        var cd = JSON.parse(card3.querySelector('[data-card-data]').textContent);
        if (!pj) return;
        var productJson = JSON.parse(pj.textContent);
        var variantId = String(cd.selected);
        whenStoqReady(function () {
          var rr = window._RestockRocket;
          /* Store-wide STOQ is set to the Embedded form type, so render the
             embedded signup form inside OUR dialog on this page — the global
             setting (and the product pages) stay untouched. */
          if (typeof rr.openInlineForm === 'function') {
            var dlg = Modal.open('notify');
            var slot = dlg && dlg.querySelector('[data-stoq-slot]');
            if (slot) {
              slot.textContent = '';
              /* STOQ's API requires a CSS SELECTOR STRING for the container
                 (a DOM element silently renders nothing — confirmed by STOQ
                 support). The slot exists in the DOM before this call. */
              try {
                rr.openInlineForm(productJson, variantId, undefined, '#' + slot.id, 'beforeend');
                return;
              } catch (e1) { Modal.close(); }
            } else if (dlg) {
              Modal.close();
            }
          }
          /* Fallback: STOQ's popup API (works when form type is Button + Popup) */
          if (typeof rr.openModal === 'function') rr.openModal(productJson, variantId);
        });
        return;
      }

      var cardStep = t.closest('[data-card-dec]') || t.closest('[data-card-inc]');
      if (cardStep) {
        var qc = cardStep.closest('[data-card]');
        var vEl = qc.querySelector('[data-card-qtyval]');
        var cur = cardQty(vEl);
        vEl.value = clampQty(t.closest('[data-card-inc]') ? cur + 1 : cur - 1);
        return;
      }

      var add = t.closest('[data-add]');
      if (add) {
        var card2 = add.closest('[data-card]');
        var data2 = JSON.parse(card2.querySelector('[data-card-data]').textContent);
        var v2 = data2.variants.find(function (x) { return String(x.id) === String(data2.selected); }) || data2.variants[0];
        if (!v2 || v2.available === false) return;
        var qEl = card2.querySelector('[data-card-qtyval]');
        var addQty = qEl ? cardQty(qEl) : 1;
        addItem(v2.id, addQty);
        if (qEl) qEl.value = 1;
        add.setAttribute('data-flash', '');
        setTimeout(function () { add.removeAttribute('data-flash'); }, 300);
        return;
      }

      /* Anywhere else on a bucket card opens its info popup — except links
         (the product-name link), buttons, the swatch row and the stepper. */
      var cardHit = t.closest('[data-card]');
      if (cardHit && Date.now() - (cardHit.__swipedAt || 0) < 350) return; /* that was a swipe */
      if (cardHit && !t.closest('a, button, input, select, label, .bb__swatches, .bb__cardqty, .bb__thumbs')) {
        openProduct(cardHit.dataset.productTpl);
        return;
      }

      if (t.closest('[data-viewcart]')) { goToCart(); return; }
      if (t.closest('[data-checkout]') || t.closest('[data-addtocart]')) { goToCheckout(); return; }
    });

    /* Direct bindings: drawer toggle and clear — immune to bubbling interference */
    var suppressCollapse = 0;
    var swipedAt = 0;
    var toggleBtn = drawer ? drawer.querySelector('[data-drawer-toggle]') : null;
    if (toggleBtn) {
      toggleBtn.addEventListener('click', function (ev) {
        ev.preventDefault(); ev.stopPropagation();
        if (Date.now() - swipedAt < 350) return; /* a swipe isn't a tap */
        state.collapsed = !state.collapsed;
        render();
      });
      /* Swipe the orange bar: up expands the drawer to fit its contents
         (capped below full screen); down steps back to normal, then closed */
      var touchY = null;
      toggleBtn.addEventListener('touchstart', function (ev) {
        touchY = ev.touches && ev.touches[0] ? ev.touches[0].clientY : null;
      }, { passive: true });
      toggleBtn.addEventListener('touchend', function (ev) {
        if (touchY === null) return;
        var endY = ev.changedTouches && ev.changedTouches[0] ? ev.changedTouches[0].clientY : touchY;
        var delta = touchY - endY; /* positive = swiped up */
        touchY = null;
        if (Math.abs(delta) < 30) return; /* treat as a tap */
        swipedAt = Date.now();
        if (delta > 0) {
          state.collapsed = false;
          state.expanded = true;
        } else if (state.expanded && !state.collapsed) {
          state.expanded = false;
        } else {
          state.collapsed = true;
        }
        render();
      });
    }
    $$('[data-clear]').forEach(function (btn) {
      btn.addEventListener('click', function (ev) {
        ev.preventDefault(); ev.stopPropagation(); suppressCollapse = Date.now();
        clearBundle();
      });
    });

    /* Outside tap collapses the mobile drawer — never for the drawer's own
       controls, whose buttons get replaced mid-click by the re-render */
    document.addEventListener('click', function (e) {
      if (!drawer || drawer.hidden || state.collapsed || !isMobile()) return;
      if (Date.now() - suppressCollapse < 400) return;
      if (e.target.closest('[data-inc], [data-dec], [data-line-qty], [data-drawer-toggle], [data-clear]')) return;
      if (!drawer.contains(e.target) && !e.target.closest('[data-add], [data-upgrade]')) {
        state.collapsed = true;
        render();
      }
    });

    /* ---------- Boot: paint, then hydrate from the real cart ----------
       Live-cart model: the cart is the source of truth, so always read it back
       on load (reflects buckets added earlier, or edits made in the cart). */
    render();
    refresh().catch(function () {});

    /* Returning from the cart page via back button (bfcache) — re-hydrate
       so cart-page edits are reflected in the drawer immediately */
    window.addEventListener('pageshow', function (e) {
      if (e.persisted) refreshIfClean();
    });
    /* Also re-hydrate when the tab regains focus (cart edited in another tab) */
    document.addEventListener('visibilitychange', function () {
      if (!document.hidden) refreshIfClean();
    });
  }

  /* ---------- Boot: all bundle roots on the page ---------- */
  function initAll(scope) {
    Array.prototype.slice.call((scope || document).querySelectorAll('[data-bb]')).forEach(initBundleBuilder);
  }
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', function () { initAll(); });
  } else {
    initAll();
  }
  /* Theme editor: re-init when the merchant tweaks section settings */
  document.addEventListener('shopify:section:load', function (e) { initAll(e.target); });
})();
